import type { DeepPartial } from '@reduxjs/toolkit';

import {
  ToothFullHeadMPR,
  ToothMPRSliceOptions,
  ToothMPRSliceOptions_SeriesOptions,
} from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';

import {
  DEFAULT_TOOTH_MPR_SERIES_OPTIONS,
  type ToothMPRSliceOptionsRequest,
} from '../config';

// Builds the full-replacement options request for a queued write. The RPC
// replaces all three series at once, so the non-target series must come from
// the freshest authoritative source — the previous queued response when it is
// newer than the store, otherwise the store. When neither has stored options,
// the protocol defaults apply: pending values of an earlier failed request must
// not ride along with the next write.
export const resolveToothMPRSliceOptionsWrite = ({
  previous,
  request,
  stored,
}: {
  previous?: ToothFullHeadMPR;
  request: DeepPartial<ToothMPRSliceOptionsRequest>;
  stored?: ToothFullHeadMPR;
}) => {
  const { series, ...rpcRequest } = request;
  const action = request.Action;

  if (!series || action?.case !== 'SetOptions') {
    return rpcRequest;
  }

  const current =
    previous &&
    (!stored ||
      (previous.Revision?.Number ?? 0) > (stored.Revision?.Number ?? 0))
      ? previous
      : stored;
  const options = current?.MPRSliceOptions;
  const fallbackSeriesOptions = () =>
    new ToothMPRSliceOptions_SeriesOptions(DEFAULT_TOOTH_MPR_SERIES_OPTIONS);

  return {
    ...rpcRequest,
    Action: {
      case: 'SetOptions' as const,
      value: new ToothMPRSliceOptions({
        Axial: options?.Axial ?? fallbackSeriesOptions(),
        VestibuloOral: options?.VestibuloOral ?? fallbackSeriesOptions(),
        MesioDistal: options?.MesioDistal ?? fallbackSeriesOptions(),
        [series]: new ToothMPRSliceOptions_SeriesOptions(
          action.value?.[series],
        ),
      }),
    },
  };
};
